import { Component, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, takeUntil } from 'rxjs';
import { CareTeamService } from 'src/app/services/careTeam.service';
import { CareTeamTemplateService } from 'src/app/services/careTeamTemplate.service';
import { ICareTeamTemplateListItem } from 'src/app/interfaces/ICareTeamTemplate';
import { ICareTeam } from 'src/app/interfaces/CareTeam/ICareTeam';
import { SwalToastService } from 'src/app/services/swal.service';
import { dynamicDateRangeValidator } from 'src/date/date.configuration';

@Component({
  selector: 'app-care-team-add',
  templateUrl: './care-team-add.component.html',
  styleUrls: ['./care-team-add.component.css'],
  standalone: false,
})
export class CareTeamAddComponent {
  private destroy$ = new Subject<void>();

  isVisible = false;
  isLoading = false;

  careTeamSubmitted = false;

  isEdit = false;
  isReadOnly = false;
  editCareTeamId?: number;
  isTemplateEditable = true;

  templateOptions: ICareTeamTemplateListItem[] = [];

  @ViewChild('modalRef', { static: true }) modalRef!: NzModalRef;

  careTeamForm: FormGroup = new FormGroup({});

  constructor(
    private readonly careTeamService: CareTeamService,
    private readonly careTeamTemplateService: CareTeamTemplateService,
    private readonly formBuilder: FormBuilder,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService
  ) {}

  ngOnInit() {
    this.isLoading = false;

    this.careTeamForm = this.formBuilder.group({
      careTeamTemplateId: [null, Validators.required],
      onSetDateTime: [
        null,
        dynamicDateRangeValidator(
          undefined,
          () => this.careTeamForm?.get('endDateTime')?.value || undefined
        ),
      ],
      endDateTime: [
        null,
        dynamicDateRangeValidator(
          () => this.careTeamForm?.get('onSetDateTime')?.value || undefined,
          undefined
        ),
      ],
    });

    this.careTeamForm.get('onSetDateTime')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.careTeamForm.get('endDateTime')?.updateValueAndValidity({ emitEvent: false });
      });

    this.careTeamForm.get('endDateTime')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.careTeamForm.get('onSetDateTime')?.updateValueAndValidity({ emitEvent: false });
      });

    this.careTeamTemplateService.getCareTeamTemplates(1, 100).subscribe({
      next: (res) => {
        this.templateOptions = res?.data || [];
      },
      error: (err) => {
        console.error('Error fetching care team templates:', err);
      },
    });

    this.careTeamService.careTeamModalSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((showModal) => {
        this.isVisible = showModal;
      });

    this.careTeamService.careTeamReadOnlySub
      .pipe(takeUntil(this.destroy$))
      .subscribe((readOnly) => {
        this.isReadOnly = readOnly;
        this.syncReadOnlyState();
      });

    this.careTeamService.careTeamEditSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((careTeam?: ICareTeam) => {
        if (careTeam) {
          this.isEdit = true;
          this.editCareTeamId = careTeam.id;
          this.isTemplateEditable = !careTeam.isSubmitted;

          this.careTeamForm.patchValue({
            careTeamTemplateId: careTeam.careTeamTemplateId,
            onSetDateTime: careTeam.onSetDateTime
              ? careTeam.onSetDateTime.slice(0, 10)
              : null,
            endDateTime: careTeam.endDateTime
              ? careTeam.endDateTime.slice(0, 10)
              : null,
          });

          if (!this.isTemplateEditable) {
            this.careTeamForm.get('careTeamTemplateId')?.disable();
          } else {
            this.careTeamForm.get('careTeamTemplateId')?.enable();
          }
        } else {
          this.isEdit = false;
          this.editCareTeamId = undefined;
          this.isTemplateEditable = true;
          this.careTeamForm.get('careTeamTemplateId')?.enable();
        }

        this.syncReadOnlyState();
      });
  }

  get editorTitle(): string {
    if (this.isReadOnly) {
      return 'View Care Team';
    }

    return this.isEdit ? 'Edit Care Team' : 'Add Care Team';
  }

  private syncReadOnlyState() {
    if (this.isReadOnly) {
      this.careTeamForm.disable({ emitEvent: false });
    } else {
      this.careTeamForm.enable({ emitEvent: false });

      if (!this.isTemplateEditable) {
        this.careTeamForm.get('careTeamTemplateId')?.disable({ emitEvent: false });
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();

    this?.modalRef.destroy();
  }

  handleCancel() {
    this.careTeamForm.reset();
    this.careTeamForm.enable();

    this.careTeamService.careTeamModalSub.next(false);
    this.careTeamService.careTeamEditSub.next(undefined);
    this.careTeamService.careTeamReadOnlySub.next(false);

    this.careTeamSubmitted = false;
    this.isEdit = false;
    this.isReadOnly = false;
    this.editCareTeamId = undefined;
    this.isTemplateEditable = true;

    this.isLoading = false;

    this.modalRef.destroy();
  }

  submitForm() {
    if (this.isReadOnly) {
      return;
    }

    this.isLoading = true;
    this.careTeamSubmitted = true;

    if (this.careTeamForm.valid) {
      const formValue = this.careTeamForm.getRawValue();
      const careTeamTemplateId = formValue?.careTeamTemplateId;
      const onSetDateTime = formValue?.onSetDateTime || undefined;
      const endDateTime = formValue?.endDateTime || undefined;

      const request = this.isEdit
        ? this.careTeamService.updateCareTeam(
            this.editCareTeamId!.toString(),
            careTeamTemplateId,
            onSetDateTime,
            endDateTime
          )
        : this.careTeamService.assignCareTeam(
            careTeamTemplateId,
            onSetDateTime,
            endDateTime
          );

      request.subscribe({
        next: () => {
          this.swalToastService.toastSuccess(
            this.isEdit
              ? 'Care Team updated successfully'
              : 'Care Team assigned successfully'
          );
          this.careTeamService.getData();

          this.handleCancel();
        },
        error: (err) => {
          if (err?.status === 409) {
            this.swalToastService.toastError(
              'This care team template is already assigned to this episode of care'
            );
          } else if (err?.status === 400) {
            this.swalToastService.toastError(
              err.error?.message || 'End date must be in the future'
            );
          } else if (err?.status === 404) {
            this.swalToastService.toastError(
              err.error?.message ||
                (this.isEdit
                  ? 'Unable to update care team'
                  : 'Unable to assign care team')
            );
          } else if (err?.status === 403) {
            this.swalToastService.toastError(
              err.error?.message || 'You cannot edit this care team'
            );
          } else {
            this.swalToastService.toastError(
              this.isEdit
                ? 'Unable to update care team'
                : 'Unable to assign care team'
            );
          }

          this.isLoading = false;
        },
      });
    } else {
      this.isLoading = false;
    }
  }

  get isSaveDisabled(): boolean {
    if (this.isLoading) return true;
    if (this.isEdit) return !this.careTeamForm.dirty;
    const v = this.careTeamForm.value;
    return !v.careTeamTemplateId || !v.onSetDateTime || !v.endDateTime || this.careTeamForm.invalid;
  }

  getCurrentDate(): string {
    return new Date().toISOString().split('T')[0];
  }
}
